import React, { useState, useEffect, useRef } from 'react';
import { usePositionContext } from '../contexts/PositionContext';
import { useOrderContext } from '../contexts/OrderContext';
import { useAccountType } from '../contexts/AccountTypeContext';
import { useAccount } from '../contexts/AccountContext';
import { useTheme } from '../contexts/ThemeContext';
import { useBackendAITrading } from '../contexts/BackendAITradingContext';
import { getAssetDecimalPlaces } from '../utils/priceFormatter';
import NumberInput from './NumberInput';

/**
 * Show a quantity to the same precision the asset's price is quoted at, so the two columns
 * of a row read as one number rather than "0.5" beside "1,919.71".
 */
const formatQuantity = (size: string, symbol: string, referencePrice: string): string => {
  const quantity = parseFloat(size);
  if (!isFinite(quantity)) return size;

  return quantity.toFixed(getAssetDecimalPlaces(symbol, parseFloat(referencePrice)));
};

/**
 * Trades in these tables are same-day or recent, so the year is noise that pushes the column
 * onto a second line.
 */
const formatTradeTime = (value: number | string | Date): string =>
  new Date(value).toLocaleString('en-US', {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true
  });

interface PositionPanelProps {
  isRightPanelVisible?: boolean;
  onExpandedChange?: (isExpanded: boolean) => void;
  isFullscreen?: boolean;
  onFullscreenToggle?: () => void;
  hasBeenManuallyResized?: boolean;
  positionPanelHeight?: number;
  isMobile?: boolean;
}

const PositionPanel: React.FC<PositionPanelProps> = ({
  isRightPanelVisible = false,
  onExpandedChange,
  isFullscreen = false,
  onFullscreenToggle,
  hasBeenManuallyResized = false,
  positionPanelHeight = 60,
  isMobile = false,
}) => {
  const [activeTab, setActiveTab] = useState<string | null>(isMobile ? 'open-positions' : null);
  const [isPositionsExpanded, setIsPositionsExpanded] = useState(isMobile);
  const [isManuallyExpanded, setIsManuallyExpanded] = useState(isMobile);
  const [editingOrderId, setEditingOrderId] = useState<string | null>(null);
  const [editPrice, setEditPrice] = useState('');
  const [editSize, setEditSize] = useState('');
  
  // Get position data from context
  const { 
    openPositions, 
    pendingOrders, 
    closedPositions, 
    closePosition, 
    cancelOrder,
    editOrder,
    closingPositions
  } = usePositionContext();
  
  // Import AI trading context to show active AI trades summary
  const { getAllActiveAssets } = useBackendAITrading();

  // Get order context for selected price
  const { selectedPrice, clearSelectedPrice } = useOrderContext();

  // Get account type and account data
  const { accountType } = useAccountType();
  const { getAccountBalance } = useAccount();
  
  // Get account-specific balance data
  const accountBalance = getAccountBalance(accountType);
  
  // Get active AI assets for summary
  const activeAIAssets = getAllActiveAssets();
  const aiPositions = openPositions.filter(pos => pos.isAI);

  // Track previous position count for auto-expand
  const prevPositionCountRef = useRef(openPositions.length);
  const autoExpandTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Auto-fill price when selected from order book
  useEffect(() => {
    if (selectedPrice && editingOrderId) {
      setEditPrice(selectedPrice.toString());
      clearSelectedPrice(); // Clear the selected price after using it
    }
  }, [selectedPrice, editingOrderId, clearSelectedPrice]);

  // Auto-expand when new positions are added (disabled in fullscreen mode)
  useEffect(() => {
    // Don't auto-expand when in fullscreen mode
    if (isFullscreen) {
      return;
    }

    const currentPositionCount = openPositions.length;
    const prevPositionCount = prevPositionCountRef.current;

    // Check if a new position was added (count increased)
    if (currentPositionCount > prevPositionCount && currentPositionCount > 0) {
      
      // Clear any existing timeout
      if (autoExpandTimeoutRef.current) {
        clearTimeout(autoExpandTimeoutRef.current);
      }

      // Auto-expand the panel and set active tab to open-positions
      setActiveTab('open-positions');
      setIsPositionsExpanded(true);
      setIsManuallyExpanded(false); // Mark as automatic expansion
      onExpandedChange?.(true);

      // No auto-collapse - panel stays expanded permanently after trade execution
    }

    // Update the previous count
    prevPositionCountRef.current = currentPositionCount;

    // Cleanup timeout on unmount
    return () => {
      if (autoExpandTimeoutRef.current) {
        clearTimeout(autoExpandTimeoutRef.current);
      }
    };
  }, [openPositions.length, onExpandedChange, isFullscreen]);

  // Auto-collapse when there are no positions and panel is expanded (only if not manually expanded, disabled in fullscreen)
  useEffect(() => {
    // Don't auto-collapse when in fullscreen mode
    if (isFullscreen) {
      return;
    }

    if (openPositions.length === 0 && isPositionsExpanded && !isManuallyExpanded) {
      // Small delay to allow user to see the "No open positions" message
      const collapseTimeout = setTimeout(() => {
        setActiveTab(null);
        setIsPositionsExpanded(false);
        onExpandedChange?.(false);
      }, 2000); // Collapse after 2 seconds

      return () => clearTimeout(collapseTimeout);
    }
  }, [openPositions.length, isPositionsExpanded, isManuallyExpanded, onExpandedChange, isFullscreen]);



  const handleClosePosition = (positionId: string) => {
    // For demo purposes, use current price as close price
    // In real implementation, this would get the current market price
    const position = openPositions.find(p => p.id === positionId);
    if (position) {
      closePosition(positionId, position.current);
    }
  };

  const handleEditOrder = (orderId: string) => {
    const order = pendingOrders.find(o => o.id === orderId);
    if (order) {
      setEditingOrderId(orderId);
      // For stop orders, edit the stopPrice (trigger price); for limit orders, edit the price
      const priceToEdit = order.orderType === 'stop' && order.stopPrice ? order.stopPrice : order.price;
      setEditPrice(priceToEdit);
      setEditSize(order.size);
    }
  };

  const handleSaveEdit = () => {
    if (editingOrderId && editPrice && editSize) {
      const order = pendingOrders.find(o => o.id === editingOrderId);
      const currentDisplayPrice = order?.orderType === 'stop' && order?.stopPrice ? order.stopPrice : order?.price;
      // console.log(`Editing order ${editingOrderId}: ${order?.symbol} ${order?.type} ${order?.orderType} - Old price: ${currentDisplayPrice}, New price: ${editPrice}`);
      editOrder(editingOrderId, editPrice, editSize);
      setEditingOrderId(null);
      setEditPrice('');
      setEditSize('');
    }
  };

  const handleCancelEdit = () => {
    setEditingOrderId(null);
    setEditPrice('');
    setEditSize('');
  };

  const handleCancelOrder = (orderId: string) => {
    cancelOrder(orderId);
  };

  const tabs = [
    { 
      id: 'open-positions', 
      name: openPositions.length > 0 ? `Positions (${openPositions.length})` : 'Positions', 
      icon: 'TrendingUp',
      count: null
    },
    { 
      id: 'pending-orders', 
      name: pendingOrders.length > 0 ? `Order (${pendingOrders.length})` : 'Order', 
      icon: 'Clock',
      count: null
    },
    { 
      id: 'history', 
      name: 'History', 
      icon: 'History',
      count: null,
      hideOnSmall: true // Flag to hide on small screens
    },
    { 
      id: 'finance', 
      name: 'Account Summary', 
      icon: 'Wallet',
      count: null, // Finance tab doesn't need a count
      hideOnSmall: true // Flag to hide on small screens
    },
    { 
      id: 'close-position', 
      name: closedPositions.length > 0 ? `Closed (${closedPositions.length})` : 'Closed', 
      icon: 'X',
      count: null
    },
    { 
      id: 'trading-panel', 
      name: 'Trading Panel', 
      icon: 'TrendingUp',
      count: null,
      hideOnSmall: true // Flag to hide on small screens
    },
  ];

  // Get finance data based on current account type
  const getFinanceData = () => {
    // Use the account-specific balance data directly
    const balance = accountBalance.totalBalance;
    const equity = accountBalance.equity;
    const margin = accountBalance.margin;
    const freeMargin = accountBalance.freeMargin;
    const marginLevel = accountBalance.marginLevel;
    const profit = accountBalance.profit;

    // Calculate different profit values for demo vs live accounts
    const baseProfit = accountType === 'demo' ? profit * 1.2 : profit; // Demo shows slightly higher profits
    const weeklyProfit = baseProfit * 0.3; // Weekly is 30% of base profit
    const monthlyProfit = baseProfit * 1.8; // Monthly is 180% of base profit

    return {
      balance: `$${balance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      equity: `$${equity.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      margin: `$${margin.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      freeMargin: `$${freeMargin.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      marginLevel: `${marginLevel.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`,
      weeklyProfit: weeklyProfit >= 0 ? `+$${weeklyProfit.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : `-$${Math.abs(weeklyProfit).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      monthlyProfit: monthlyProfit >= 0 ? `+$${monthlyProfit.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : `-$${Math.abs(monthlyProfit).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    };
  };

  const financeData = getFinanceData();
  const { theme } = useTheme();

  // Calculate scrollable container height based on panel state
  const getScrollableHeight = () => {
    if (isMobile) {
      // Mobile: Min height 250px, max height 250px with scroll
      const hasPositions = openPositions.length > 0 || pendingOrders.length > 0 || closedPositions.length > 0;
      if (hasPositions) {
        // If there are positions, use max height with scroll
        return { height: '250px', maxHeight: '250px' };
      } else {
        // If no positions, use min height - just show the tab header
        return { height: '250px', maxHeight: '250px' };
      }
    } else if (isFullscreen) {
      return { height: '555px' }; // Fullscreen height
    } else if (hasBeenManuallyResized && isPositionsExpanded) {
      // When manually resized, calculate height based on panel height
      // Subtract: tab header (~40px) + table header (~30px) + padding (~30px) = ~100px
      const headerAndPadding = 100;
      const availableHeight = Math.max(positionPanelHeight - headerAndPadding, 120); // Minimum 120px for scrollability
      
      // Ensure the height is reasonable and allows for scrolling
      // Allow up to 80% of viewport height for maximum usability
      const viewportHeight = window.innerHeight;
      const maxAllowedHeight = Math.floor(viewportHeight * 0.8); // 80% of screen height
      const maxHeight = Math.min(availableHeight, maxAllowedHeight);
      
      // Debug logging
      
      return { height: `${maxHeight}px` };
    } else if (isPositionsExpanded) {
      return { height: '70px' }; // Default expanded height (2 rows)
    } else {
      return { maxHeight: '96px' }; // Collapsed height (24 * 4px = 96px)
    }
  };

  // Mock close positions data is now replaced by real data from PositionContext

  return (
    <div className="m-0 p-0">
    <div 
      className={`theme-bg-secondary rounded-lg ${isMobile ? '' : 'max-h-[40px]'}`}
      style={isMobile ? getScrollableHeight() : {}}
    >
      {/* Tab Navigation */}
      <div className="border-b theme-border">
        <div className="flex items-center justify-between px-2 mt-2">
          <nav className="flex space-x-1 lg:space-x-8 overflow-x-auto scrollbar-hide" aria-label="Tabs">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => {
                  // Toggle behavior: if same tab is clicked, close it; otherwise open the new tab
                  if (activeTab === tab.id) {
                    setActiveTab(null);
                    setIsPositionsExpanded(false);
                    setIsManuallyExpanded(false);
                    onExpandedChange?.(false);
                  } else {
                    setActiveTab(tab.id);
                    setIsPositionsExpanded(true);
                    setIsManuallyExpanded(true);
                    onExpandedChange?.(true);
                    
                    // Auto-enable fullscreen for Trading Panel tab
                    if (tab.id === 'trading-panel' && !isFullscreen) {
                      onFullscreenToggle?.();
                    }
                  }
                }}
                className={`flex items-center space-x-1 md:space-x-2 py-2 md:py-3 px-1 md:px-1 border-b-2 font-medium text-xs transition-colors duration-200 whitespace-nowrap flex-1 md:flex-none ${tab.hideOnSmall ? 'flex sm:hidden md:flex' : ''} ${
                  activeTab === tab.id
                    ? 'border-[#2AB7A6] text-[#2AB7A6]'
                    : `border-transparent theme-text-quaternary ${
                        theme === 'light' 
                          ? 'hover:text-[#2AB7A6] hover:border-[#2AB7A6]' 
                          : 'hover:text-white hover:theme-border-secondary'
                      }`
                }`}
              >
                <span className="hidden lg:inline">{tab.name}</span>
                <span className="lg:hidden text-center w-full">{tab.name.split(' ')[0]}</span>
              </button>
            ))}
          </nav>
          
          {/* Control Buttons */}
          <div className="flex items-center space-x-1">
            {/* Fullscreen Toggle Button */}
            <button
              onClick={() => {
                onFullscreenToggle?.();
                // Automatically activate first tab when entering fullscreen
                if (!isFullscreen) {
                  setActiveTab('open-positions');
                  setIsPositionsExpanded(true);
                  setIsManuallyExpanded(true);
                  onExpandedChange?.(true);
                }
              }}
              className="p-1 theme-text-quaternary hover:theme-text-secondary transition-colors duration-200"
              title={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
            >
              {isFullscreen ? (
                // Exit fullscreen icon
                <svg 
                  className="w-4 h-4 transition-colors duration-200" 
                  fill="none" 
                  stroke="currentColor" 
                  strokeWidth="2" 
                  viewBox="0 0 24 24"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    d="M9 9V4.5M9 9H4.5M9 9L3.5 3.5M15 9v4.5M15 9h4.5M15 9l5.5-5.5M9 15v4.5M9 15H4.5M9 15l-5.5 5.5M15 15v-4.5M15 15h4.5M15 15l5.5 5.5" 
                    className="transition-colors duration-200 group-hover:stroke-[#2AB7A6]"
                  />
                </svg>
              ) : (
                // Enter fullscreen icon
                <svg 
                  className="w-4 h-4 transition-colors duration-200" 
                  fill="none" 
                  stroke="currentColor" 
                  strokeWidth="2" 
                  viewBox="0 0 24 24"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    d="M4 8V4h4M20 8V4h-4M4 16v4h4M20 16v4h-4" 
                    className="transition-colors duration-200 group-hover:stroke-[#2AB7A6]"
                  />
                </svg>
              )}
            </button>
            
            {/* Collapse/Expand Toggle Button - Hidden in fullscreen mode */}
            {!isFullscreen && (
              <button
                onClick={() => {
                  // Clear auto-collapse timeout when manually toggling
                  if (autoExpandTimeoutRef.current) {
                    clearTimeout(autoExpandTimeoutRef.current);
                    autoExpandTimeoutRef.current = null;
                  }
                  
                  // Toggle expand/collapse state
                  const newExpanded = !isPositionsExpanded;
                  setIsPositionsExpanded(newExpanded);
                  setIsManuallyExpanded(newExpanded); // Mark as manual expansion/collapse
                  if (!newExpanded) {
                    setActiveTab(null); // Clear active tab when collapsing
                  } else {
                    // Automatically activate first tab when expanding
                    setActiveTab('open-positions');
                  }
                  onExpandedChange?.(newExpanded);
                }}
                className="p-1 theme-text-quaternary hover:theme-text-secondary transition-colors duration-200"
                title={isPositionsExpanded ? "Collapse panel" : "Expand panel"}
              >
              <svg 
                className={`w-4 h-4 transition-transform duration-200 ${isPositionsExpanded ? 'rotate-0' : 'rotate-180'}`}
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M19 9l-7 7-7-7" 
                />
              </svg>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <div className={`p-3 pt-1 transition-all duration-300 ${
        activeTab && (isFullscreen || isPositionsExpanded)
          ? 'h-full overflow-y-auto' // Show content when tab is active and expanded
          : 'max-h-0 overflow-hidden' // Hide content when no tab is active
      }`}>
        {activeTab === 'open-positions' && (
          <div className={`${isFullscreen ? 'h-full flex flex-col' : 'space-y-2'}`}>
            {/* Table Header */}
            {/* The rows below carry `px-2`, so the header needs it too: without it every
                header label sat two pixels left of its column, which showed up most on the
                right-aligned Action label hanging past the Close button beneath it. */}
            <div className="grid grid-cols-1 md:grid-cols-10 gap-2 text-xs font-medium theme-text-quaternary border-b theme-border-secondary pb-2 mt-3 px-2 flex-shrink-0">
              {/* Mobile Header - Full width with justify-between */}
              <div className="md:hidden flex justify-between items-center">
                <span className="font-medium">Symbol</span>
                <span className="font-medium">Type</span>
                <span className="font-medium">Size</span>
                <span className="font-medium">P&L</span>
                <span className="font-medium">Action</span>
              </div>
              {/* Desktop Header - 10 column grid */}
              <div className="hidden md:contents">
                <div className="font-medium">Symbol</div>
                <div className="font-medium">Type</div>
                <div className="font-medium">Size</div>
                <div className="font-medium">Entry Price</div>
                <div className="font-medium">Price</div>
                <div className="font-medium">Margin</div>
                <div className="font-medium">Lev</div>
                <div className="font-medium">P&L</div>
                <div className="font-medium">Time</div>
                <div className="text-right font-medium">Action</div>
              </div>
            </div>

            {/* Table Rows with Scrollable Container */}
            <div 
              className="overflow-y-auto scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-200"
              style={{
                ...getScrollableHeight(),
                overflowY: 'auto',
                overflowX: 'hidden',
                position: 'relative'
              }}
            >
              {openPositions.length === 0 ? (
                <div className="text-center py-5 text-xs theme-text-quaternary ">
                  <span className="py-4">No open positions</span>
                </div>
              ) : (
                openPositions.map(position => {
                  // Calculate margin used for this position
                  const entryPrice = parseFloat(position.entry);
                  const size = parseFloat(position.size);
                  const leverage = position.leverage;
                  const marginUsed = (entryPrice * size) / leverage;
                  
                  return (
                    <div
                      key={position.id}
                      className="grid grid-cols-1 md:grid-cols-10 gap-2 text-xs py-2 px-2 border-b theme-border hover:theme-bg-quaternary/50 transition-colors duration-200 items-center"
                    >
                      {/* Mobile Row - Full width with justify-between */}
                      <div className="md:hidden flex justify-between items-center">
                        <div className="flex items-center">
                          <span className="font-medium theme-text-secondary">{position.symbol}</span>
                          {position.isAI ? (
                            <span className="ml-1 px-1 py-0.5 text-xs bg-purple-500/20 text-purple-400 rounded" title="AI Trade">
                              AI
                            </span>
                          ) : (
                            <span className="ml-1 px-1 py-0.5 text-xs bg-green-500/20 text-green-400 rounded" title="Manual Trade">
                              MI
                            </span>
                          )}
                        </div>
                        <div className="flex items-center">
                          <div
                            className={`p-1 rounded mr-2 ${position.type === 'Buy' ? 'bg-[#2AB7A6]/20' : 'bg-red-500/20'}`}
                          >
                            <span className={`text-xs ${position.type === 'Buy' ? 'text-[#2AB7A6]' : 'text-red-400'}`}>
                              {position.type === 'Buy' ? '↗' : '↘'}
                            </span>
                          </div>
                          <span
                            className={
                              position.type === 'Buy'
                                ? 'text-[#2AB7A6]'
                                : 'text-red-400'
                            }
                          >
                            {position.type}
                          </span>
                        </div>
                        <span className="theme-text-secondary">
                          {formatQuantity(position.size, position.symbol, position.entry)}
                        </span>
                        <div className={`font-medium whitespace-nowrap ${position.pnl.startsWith('+') ? 'text-[#2AB7A6]' : 'text-red-400'}`}>
                          {position.pnl}
                        </div>
                        <button
                          onClick={() => handleClosePosition(position.id)}
                          disabled={closingPositions.has(position.id)}
                          className={`px-2 py-1 text-xs font-medium rounded transition-colors duration-200 ${
                            closingPositions.has(position.id)
                              ? 'bg-gray-500/20 text-gray-400 cursor-not-allowed'
                              : 'bg-red-500/20 text-red-400 hover:bg-red-500/30 hover:text-red-300'
                          }`}
                          title="Close Position"
                        >
                          {closingPositions.has(position.id) ? (
                            <div className="flex items-center space-x-1">
                              <div className="w-3 h-3 border border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                              <span>Closing...</span>
                            </div>
                          ) : (
                            'Close'
                          )}
                        </button>
                      </div>
                      {/* Desktop Row - 10 column grid */}
                      <div className="hidden md:contents">
                        <div className="font-medium theme-text-secondary flex items-center">
                          {position.symbol}
                          {position.isAI ? (
                            <span className="ml-1 px-1 py-0.5 text-xs bg-purple-500/20 text-purple-400 rounded" title="AI Trade">
                              AI
                            </span>
                          ) : (
                            <span className="ml-1 px-1 py-0.5 text-xs bg-green-500/20 text-green-400 rounded" title="Manual Trade">
                              MI
                            </span>
                          )}
                        </div>
                        <div className="flex items-center">
                          <div
                            className={`p-1 rounded mr-2 ${position.type === 'Buy' ? 'bg-[#2AB7A6]/20' : 'bg-red-500/20'}`}
                          >
                            <span className={`text-xs ${position.type === 'Buy' ? 'text-[#2AB7A6]' : 'text-red-400'}`}>
                              {position.type === 'Buy' ? '↗' : '↘'}
                            </span>
                          </div>
                          <span
                            className={
                              position.type === 'Buy'
                                ? 'text-[#2AB7A6]'
                                : 'text-red-400'
                            }
                          >
                            {position.type}
                          </span>
                        </div>
                        <div className="theme-text-secondary">
                          {formatQuantity(position.size, position.symbol, position.entry)}
                        </div>
                        <div className="theme-text-secondary">${position.entry}</div>
                        <div className="theme-text-secondary">${position.current}</div>
                        <div className="theme-text-secondary">${marginUsed.toFixed(2)}</div>
                        <div className="theme-text-secondary">
                          {position.leverage}x
                        </div>
                        <div>
                          <div className={`font-medium whitespace-nowrap ${position.pnl.startsWith('+') ? 'text-[#2AB7A6]' : 'text-red-400'}`}>
                            {position.pnl} ({position.pnlPercent})
                          </div>
                        </div>
                        <div className="theme-text-secondary text-xs whitespace-nowrap">
                          {formatTradeTime(position.timestamp)}
                        </div>
                        <div className="flex justify-end">
                          <button
                            onClick={() => handleClosePosition(position.id)}
                            disabled={closingPositions.has(position.id)}
                            className={`px-2 py-1 text-xs font-medium rounded transition-colors duration-200 ${
                              closingPositions.has(position.id)
                                ? 'bg-gray-500/20 text-gray-400 cursor-not-allowed'
                                : 'bg-red-500/20 text-red-400 hover:bg-red-500/30 hover:text-red-300'
                            }`}
                            title="Close Position"
                          >
                            {closingPositions.has(position.id) ? (
                              <div className="flex items-center space-x-1">
                                <div className="w-3 h-3 border border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                                <span>Closing...</span>
                              </div>
                            ) : (
                              'Close'
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}

        {activeTab === 'pending-orders' && (
          <div className={`${isFullscreen ? 'h-full flex flex-col' : 'space-y-2'}`}>
            {/* Table Header */}
            {/* Matches the `px-2` on the rows so the columns line up. */}
            <div className="grid grid-cols-1 md:grid-cols-9 gap-2 text-xs font-medium theme-text-quaternary border-b theme-border-secondary pb-2 mt-3 px-2 flex-shrink-0">
              {/* Mobile Header - Full width with justify-between */}
              <div className="md:hidden flex justify-between items-center">
                <span className="font-medium">Symbol</span>
                <span className="font-medium">Type</span>
                <span className="font-medium">Size</span>
                <span className="font-medium">Status</span>
                <span className="font-medium">Action</span>
              </div>
              {/* Desktop Header - 9 column grid */}
              <div className="hidden md:contents">
                <div className="font-medium">Symbol</div>
                <div className="font-medium">Type</div>
                <div className="font-medium">Size</div>
                <div className="font-medium">Order Type</div>
                <div className="font-medium">Price</div>
                <div className="font-medium">Margin</div>
                <div className="font-medium">Leverage</div>
                <div className="font-medium">Status</div>
                <div className="text-right font-medium">Action</div>
              </div>
            </div>

            {/* Table Rows with Scrollable Container */}
            <div 
              className="overflow-y-auto scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-200"
              style={{
                ...getScrollableHeight(),
                overflowY: 'auto',
                overflowX: 'hidden',
                position: 'relative'
              }}
            >
              {pendingOrders.length === 0 ? (
                <div className="text-center py-4 text-xs theme-text-quaternary">
                  No pending orders
                </div>
              ) : (
                pendingOrders.map(order => {
                  // Calculate margin that would be used for this order
                  const orderPrice = parseFloat(order.orderType === 'stop' && order.stopPrice ? order.stopPrice : order.price);
                  const orderSize = parseFloat(order.size);
                  const leverage = order.leverage;
                  const marginUsed = (orderPrice * orderSize) / leverage;
                  
                  return (
                    <div
                      key={order.id}
                      className="grid grid-cols-1 md:grid-cols-9 gap-2 text-xs py-2 px-2 border-b theme-border hover:theme-bg-quaternary/50 transition-colors duration-200 items-center"
                    >
                      {/* Mobile Row - Full width with justify-between */}
                      <div className="md:hidden flex justify-between items-center">
                        <span className="font-medium theme-text-secondary">{order.symbol}</span>
                        <span
                          className={
                            order.type === 'Buy'
                              ? 'text-[#2AB7A6]'
                              : 'text-red-400'
                          }
                        >
                          {order.type}
                        </span>
                        <span className="theme-text-secondary">
                          {editingOrderId === order.id ? (
                            <NumberInput
                              value={editSize}
                              onChange={(e) => setEditSize(e.target.value)}
                              className="w-16"
                              size="sm"
                              placeholder="Size"
                            />
                          ) : (
                            formatQuantity(order.size, order.symbol, order.price)
                          )}
                        </span>
                        <span className={`px-2 py-1 text-xs font-medium rounded ${
                          order.status === 'Pending' 
                            ? 'bg-yellow-500/20 text-yellow-400' 
                            : order.status === 'Filled'
                            ? 'bg-green-500/20 text-green-400'
                            : 'bg-red-500/20 text-red-400'
                        }`}>
                          {order.status}
                        </span>
                        <div className="flex space-x-1">
                          {editingOrderId === order.id ? (
                            <>
                              <button
                                onClick={handleSaveEdit}
                                className="px-2 py-1 text-xs font-medium bg-green-500/20 text-green-400 hover:bg-green-500/30 hover:text-green-300 rounded transition-colors duration-200"
                                title="Save Changes"
                              >
                                Save
                              </button>
                              <button
                                onClick={handleCancelEdit}
                                className="px-2 py-1 text-xs font-medium bg-gray-500/20 text-gray-400 hover:bg-gray-500/30 hover:theme-text-secondary rounded transition-colors duration-200"
                                title="Cancel Edit"
                              >
                                Cancel
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                onClick={() => handleEditOrder(order.id)}
                                className="px-2 py-1 text-xs font-medium bg-[#2AB7A6]/20 text-[#2AB7A6] hover:bg-[#2AB7A6]/30 hover:text-[#2AB7A6] rounded transition-colors duration-200"
                                title="Edit Order"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => handleCancelOrder(order.id)}
                                className="px-2 py-1 text-xs font-medium bg-red-500/20 text-red-400 hover:bg-red-500/30 hover:text-red-300 rounded transition-colors duration-200"
                                title="Cancel Order"
                              >
                                Cancel
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                      {/* Desktop Row - 9 column grid */}
                      <div className="hidden md:contents">
                        <div className="font-medium theme-text-secondary">
                          {order.symbol}
                        </div>
                        <div className="flex items-center">
                          <span
                            className={
                              order.type === 'Buy'
                                ? 'text-[#2AB7A6]'
                                : 'text-red-400'
                            }
                          >
                            {order.type}
                          </span>
                        </div>
                        <div className="theme-text-secondary">
                          {editingOrderId === order.id ? (
                            <NumberInput
                              value={editSize}
                              onChange={(e) => setEditSize(e.target.value)}
                              className="w-full"
                              size="sm"
                              placeholder="Size"
                            />
                          ) : (
                            formatQuantity(order.size, order.symbol, order.price)
                          )}
                        </div>
                        <div className="theme-text-secondary">
                          <span className={`px-2 py-1 text-xs font-medium rounded ${
                            order.orderType === 'limit' 
                              ? 'bg-white/20 text-white' 
                              : 'bg-orange-500/20 text-orange-400'
                          }`}>
                            {order.orderType === 'limit' ? 'Limit' : 'Stop'}
                          </span>
                        </div>
                        <div className="theme-text-secondary">
                          {editingOrderId === order.id ? (
                            <NumberInput
                              value={editPrice}
                              onChange={(e) => setEditPrice(e.target.value)}
                              className="w-full"
                              size="sm"
                              step="0.00001"
                              placeholder="Price"
                            />
                          ) : (
                            order.orderType === 'stop' && order.stopPrice ? order.stopPrice : order.price
                          )}
                        </div>
                        <div className="theme-text-secondary">${marginUsed.toFixed(2)}</div>
                        <div className="theme-text-secondary">
                          {leverage}x
                        </div>
                        <div className="flex items-center">
                          <span className={`px-2 py-1 text-xs font-medium rounded ${
                            order.status === 'Pending' 
                              ? 'bg-yellow-500/20 text-yellow-400' 
                              : order.status === 'Filled'
                              ? 'bg-green-500/20 text-green-400'
                              : 'bg-red-500/20 text-red-400'
                          }`}>
                            {order.status}
                          </span>
                        </div>
                        <div className="flex justify-end space-x-1">
                          {editingOrderId === order.id ? (
                            <>
                              <button
                                onClick={handleSaveEdit}
                                className="px-2 py-1 text-xs font-medium bg-green-500/20 text-green-400 hover:bg-green-500/30 hover:text-green-300 rounded transition-colors duration-200"
                                title="Save Changes"
                              >
                                Save
                              </button>
                              <button
                                onClick={handleCancelEdit}
                                className="px-2 py-1 text-xs font-medium bg-gray-500/20 text-gray-400 hover:bg-gray-500/30 hover:theme-text-secondary rounded transition-colors duration-200"
                                title="Cancel Edit"
                              >
                                Cancel
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                onClick={() => handleEditOrder(order.id)}
                                className="px-2 py-1 text-xs font-medium bg-[#2AB7A6]/20 text-[#2AB7A6] hover:bg-[#2AB7A6]/30 hover:text-[#2AB7A6] rounded transition-colors duration-200"
                                title="Edit Order"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => handleCancelOrder(order.id)}
                                className="px-2 py-1 text-xs font-medium bg-red-500/20 text-red-400 hover:bg-red-500/30 hover:text-red-300 rounded transition-colors duration-200"
                                title="Cancel Order"
                              >
                                Cancel
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}

        {activeTab === 'finance' && (
          <div className="space-y-2">
            {/* Account Type Indicator */}
            <div className="flex items-center justify-center mb-2 mt-1.5">
              <div className="px-3 py-1 text-xs font-medium rounded-full bg-[#2AB7A6]/20 text-[#2AB7A6]">
                {accountType === 'demo' ? 'Demo Account' : 'Live Account'}
              </div>
            </div>
            
            {/* Account Metrics - 8 metrics in 4x2 grid */}
            <div className="mb-4">
              {/* Row 1: Balance, Margin, Equity, Free Margin */}
              <div className="grid grid-cols-4 gap-2 mb-2">
                <div className="p-3 py-2 theme-bg-quaternary rounded-lg">
                  <p className="text-xs font-medium theme-text-primary">Balance</p>
                  <p className="text-xs font-medium theme-text-primary">
                    ${accountBalance.totalBalance.toFixed(2)}
                  </p>
                </div>
                {/* Margin, free margin and margin level below are shown as they stand. They
                    used to be blanked out whenever no position was open, which papered over
                    margin that had failed to release on close instead of showing it. */}
                <div className="p-3 py-2 theme-bg-quaternary rounded-lg">
                  <p className="text-xs font-medium theme-text-primary">Margin</p>
                  <p className="text-xs font-medium text-red-400">
                    ${accountBalance.margin.toFixed(2)}
                  </p>
                </div>
                <div className="p-3 py-2 theme-bg-quaternary rounded-lg">
                  <p className="text-xs font-medium theme-text-primary">Equity</p>
                  <p className="text-xs font-medium text-[#2AB7A6]">
                    ${accountBalance.equity.toFixed(2)}
                  </p>
                </div>
                <div className="p-3 py-2 theme-bg-quaternary rounded-lg">
                  <p className="text-xs font-medium theme-text-primary">Free Margin</p>
                  <p className="text-xs font-medium text-[#2AB7A6]">
                    ${accountBalance.freeMargin.toFixed(2)}
                  </p>
                </div>
              </div>
              
              {/* Row 2: Margin Level, Leverage, Current P&L, Weekly Profit */}
              <div className="grid grid-cols-4 gap-2">
                <div className="p-3 py-2 theme-bg-quaternary rounded-lg">
                  <p className="text-xs font-medium theme-text-primary">Margin Level</p>
                  <p className="text-xs font-medium theme-text-primary">
                    {accountBalance.marginLevel.toFixed(2)}%
                  </p>
                </div>
                <div className="p-3 py-2 theme-bg-quaternary rounded-lg">
                  <p className="text-xs font-medium theme-text-primary">Leverage</p>
                  <p className="text-xs font-medium theme-text-primary">
                    {accountBalance.leverage}
                  </p>
                </div>
                <div className="p-3 py-2 theme-bg-quaternary rounded-lg">
                  <p className="text-xs font-medium theme-text-primary">Current P&L</p>
                  {/* "Current" means the account's P&L right now, so open positions count.
                      This read the realized figure alone and so sat still while a trade ran. */}
                  <p className={`text-xs font-medium ${accountBalance.totalPnL >= 0 ? 'text-[#2AB7A6]' : 'text-red-400'}`}>
                    ${accountBalance.totalPnL.toFixed(2)}
                  </p>
                </div>
                <div className="p-3 py-2 theme-bg-quaternary rounded-lg">
                  <p className="text-xs font-medium theme-text-primary">Weekly Profit</p>
                  <p className={`text-xs font-medium ${financeData.weeklyProfit.startsWith('+') ? 'text-[#2AB7A6]' : 'text-red-400'}`}>
                    {financeData.weeklyProfit}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'history' && (
          <div className={`${isFullscreen ? 'h-full flex flex-col' : 'space-y-2'}`}>
            {/* Table Header */}
            <div className="grid gap-2 text-xs font-medium theme-text-quaternary border-b theme-border-secondary pb-2 mt-3 flex-shrink-0" 
                 style={{ gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr 1.5fr 1fr 1fr' }}>
              <div className="font-medium">Symbol</div>
              <div className="font-medium">Type</div>
              <div className="font-medium">Size</div>
              <div className="font-medium">Entry</div>
              <div className="font-medium">Close</div>
              <div className="font-medium">Close Time</div>
              <div className="font-medium">P&L</div>
              <div className="font-medium">Status</div>
            </div>
            
            {/* Table Body */}
            <div className={`${isFullscreen ? 'flex-1 overflow-y-auto' : 'max-h-96 overflow-y-auto'}`}>
              {closedPositions.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-sm theme-text-quaternary">No history available</p>
                </div>
              ) : (
                closedPositions.map((position) => (
                  <div 
                    key={position.id} 
                    className="grid gap-2 text-xs py-2 border-b theme-border-secondary last:border-b-0 hover:theme-bg-quaternary transition-colors duration-150"
                    style={{ gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr 1.5fr 1fr 1fr' }}
                  >
                    <div className="font-medium theme-text-primary">{position.symbol}</div>
                    <div className={`${position.type === 'Buy' ? 'text-[#2AB7A6]' : 'text-red-400'}`}>
                      {position.type}
                    </div>
                    <div className="theme-text-primary">
                      {formatQuantity(position.size, position.symbol, position.entry)}
                    </div>
                    <div className="theme-text-primary">${position.entry}</div>
                    <div className="theme-text-primary">${position.close}</div>
                    <div className="theme-text-primary whitespace-nowrap">
                      {(() => {
                        // `closeTime` is stored already formatted, so it is re-parsed to drop
                        // the year from trades that were closed before that change.
                        if (position.closeTime && 
                            position.closeTime !== 'Invalid time' && 
                            position.closeTime !== 'Invalid Date' &&
                            !isNaN(new Date(position.closeTime).getTime())) {
                          return formatTradeTime(position.closeTime);
                        }
                        
                        // Fallback to timestamp
                        if (position.timestamp && !isNaN(new Date(position.timestamp).getTime())) {
                          return formatTradeTime(position.timestamp);
                        }
                        
                        // Final fallback to current time
                        return formatTradeTime(Date.now());
                      })()}
                    </div>
                    <div className={`font-medium whitespace-nowrap ${position.pnl.startsWith('+') ? 'text-[#2AB7A6]' : 'text-red-400'}`}>
                      {position.pnl}
                    </div>
                    <div className={`${position.status === 'Success' ? 'text-[#2AB7A6]' : 'text-red-400'}`}>
                      {position.status}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {activeTab === 'close-position' && (
          <div className={`${isFullscreen ? 'h-full flex flex-col' : 'space-y-2'}`}>
            {/* Table Header */}
            <div className="grid gap-2 text-xs font-medium theme-text-quaternary border-b theme-border-secondary pb-2 mt-3 flex-shrink-0">
              {/* Mobile Header */}
              <div className="md:hidden flex justify-between items-center px-2">
                <span className="font-medium">Symbol</span>
                <span className="font-medium">Type</span>
                <span className="font-medium">P&L</span>
                <span className="font-medium">Status</span>
              </div>
              {/* Desktop Header */}
              <div className="hidden md:grid gap-2" style={{ gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr 1.5fr 1fr 1fr' }}>
                <div className="font-medium">Symbol</div>
                <div className="font-medium">Type</div>
                <div className="font-medium">Size</div>
                <div className="font-medium">Entry</div>
                <div className="font-medium">Close</div>
                <div className="font-medium">Close Time</div>
                <div className="text-center font-medium">Status</div>
                <div className="text-right font-medium">P&L</div>
              </div>
            </div>

            {/* Table Rows with Scrollable Container */}
            <div 
              className="overflow-y-auto scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-200"
              style={{
                ...getScrollableHeight(),
                overflowY: 'auto',
                overflowX: 'hidden',
                position: 'relative'
              }}
            >
              {closedPositions.length === 0 ? (
                <div className="text-center py-4 text-xs theme-text-quaternary">
                  No closed positions
                </div>
              ) : (
                closedPositions.map(position => (
                <div
                  key={position.id}
                  className="grid gap-2 text-xs py-2 px-2 border-b theme-border hover:theme-bg-quaternary/50 transition-colors duration-200 items-center"
                >
                  {/* Mobile Row */}
                  <div className="md:hidden flex justify-between items-center">
                    <div className="flex items-center">
                      <span className="font-medium theme-text-secondary">{position.symbol}</span>
                      {position.isAI ? (
                        <span className="ml-1 px-1 py-0.5 text-xs bg-purple-500/20 text-purple-400 rounded" title="AI Trade">
                          AI
                        </span>
                      ) : (
                        <span className="ml-1 px-1 py-0.5 text-xs bg-green-500/20 text-green-400 rounded" title="Manual Trade">
                          MI
                        </span>
                      )}
                    </div>
                    <div className="flex items-center">
                      <div
                        className={`p-1 rounded mr-2 ${position.type === 'Buy' ? 'bg-[#2AB7A6]/20' : 'bg-red-500/20'}`}
                      >
                        <span className={`text-xs ${position.type === 'Buy' ? 'text-[#2AB7A6]' : 'text-red-400'}`}>
                          {position.type === 'Buy' ? '↗' : '↘'}
                        </span>
                      </div>
                      <span
                        className={
                          position.type === 'Buy'
                            ? 'text-[#2AB7A6]'
                            : 'text-red-400'
                        }
                      >
                        {position.type}
                      </span>
                    </div>
                    <div className={`font-medium ${position.pnl.startsWith('+') ? 'text-[#2AB7A6]' : 'text-red-400'}`}>
                      {position.pnl}
                    </div>
                    <div className={`px-2 py-1 text-xs font-medium rounded ${
                      position.status === 'Success' 
                        ? 'bg-[#2AB7A6]/20 text-[#2AB7A6]' 
                        : 'bg-red-500/20 text-red-400'
                    }`}>
                      {position.status}
                    </div>
                  </div>
                  {/* Desktop Row */}
                  <div className="hidden md:grid gap-2" style={{ gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr 1.5fr 1fr 1fr' }}>
                    <div className="font-medium theme-text-secondary flex items-center">
                      {position.symbol}
                      {position.isAI ? (
                        <span className="ml-1 px-1 py-0.5 text-xs bg-purple-500/20 text-purple-400 rounded" title="AI Trade">
                          AI
                        </span>
                      ) : (
                        <span className="ml-1 px-1 py-0.5 text-xs bg-green-500/20 text-green-400 rounded" title="Manual Trade">
                          MI
                        </span>
                      )}
                    </div>
                    <div className="flex items-center">
                      <div
                        className={`p-1 rounded mr-2 ${position.type === 'Buy' ? 'bg-[#2AB7A6]/20' : 'bg-red-500/20'}`}
                      >
                        <span className={`text-xs ${position.type === 'Buy' ? 'text-[#2AB7A6]' : 'text-red-400'}`}>
                          {position.type === 'Buy' ? '↗' : '↘'}
                        </span>
                      </div>
                      <span
                        className={
                          position.type === 'Buy'
                            ? 'text-[#2AB7A6]'
                            : 'text-red-400'
                        }
                      >
                        {position.type}
                      </span>
                    </div>
                    <div className="theme-text-secondary">
                      {formatQuantity(position.size, position.symbol, position.entry)}
                    </div>
                    <div className="theme-text-secondary">${position.entry}</div>
                    <div className="theme-text-secondary">${position.close}</div>
                    <div className="theme-text-secondary text-xs whitespace-nowrap">
                      {formatTradeTime(position.closeTime)}
                    </div>
                    <div className="flex justify-center">
                      <div className={`px-2 py-1 text-xs font-medium rounded ${
                        position.status === 'Success' 
                          ? 'bg-[#2AB7A6]/20 text-[#2AB7A6]' 
                          : 'bg-red-500/20 text-red-400'
                      }`}>
                        {position.status}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className={`font-medium whitespace-nowrap ${position.pnl.startsWith('+') ? 'text-[#2AB7A6]' : 'text-red-400'}`}>
                        {position.pnl} ({position.pnlPercent})
                      </div>
                    </div>
                  </div>
                </div>
                ))
              )}
            </div>
          </div>
        )}

        {activeTab === 'trading-panel' && (
          <div className={`${isFullscreen ? 'h-full flex flex-col' : 'space-y-4'}`}>
            {/* Broker Cards Grid */}
            <div className="flex justify-center">
              <div className="grid grid-cols-5 gap-6 max-w-7xl">
                {/* Paper Trading Card */}
                  <div className="border theme-border rounded-lg p-3 hover:theme-bg-quaternary/50 transition-colors duration-200 cursor-pointer aspect-square flex flex-col items-center justify-center text-center">
                  <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center mb-3">
                    <span className="text-white font-bold text-sm">PT</span>
                  </div>
                  <div className="font-medium theme-text-primary text-xs mb-2">Paper Trading</div>
                  <div className="text-xs theme-text-quaternary text-center leading-tight mb-3">brokerage simulator by Live Trading Studio</div>
                  <button className="px-3 py-1 bg-[#2AB7A6] text-white text-xs font-medium rounded hover:bg-[#239B8A] transition-colors duration-200">
                    Free
                  </button>
                </div>

                {/* Oanda Card */}
                  <div className="border theme-border rounded-lg p-3 hover:theme-bg-quaternary/50 transition-colors duration-200 cursor-pointer aspect-square flex flex-col items-center justify-center text-center">
                  <div className="w-10 h-10 bg-gradient-to-br from-red-500 to-red-600 rounded-lg flex items-center justify-center mb-3">
                    <span className="text-white font-bold text-sm">O</span>
                  </div>
                  <div className="font-medium theme-text-primary text-xs mb-2">Oanda</div>
                  <div className="text-xs theme-text-quaternary text-center leading-tight mb-3">Global forex, commodities and stock broker</div>
                  <button className="px-3 py-1 bg-[#2AB7A6] text-white text-xs font-medium rounded hover:bg-[#239B8A] transition-colors duration-200">
                    Connect
                  </button>
                </div>

                {/* Pepperstone Card */}
                  <div className="border theme-border rounded-lg p-3 hover:theme-bg-quaternary/50 transition-colors duration-200 cursor-pointer aspect-square flex flex-col items-center justify-center text-center">
                  <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg flex items-center justify-center mb-3">
                    <span className="text-white font-bold text-sm">P</span>
                  </div>
                  <div className="font-medium theme-text-primary text-xs mb-2">Pepperstone</div>
                  <div className="text-xs theme-text-quaternary text-center leading-tight mb-3">Award-winning forex, commodities, stocks broker</div>
                  <button className="px-3 py-1 bg-[#2AB7A6] text-white text-xs font-medium rounded hover:bg-[#239B8A] transition-colors duration-200">
                    Connect
                  </button>
                </div>

                {/* Fyers Card */}
                  <div className="border theme-border rounded-lg p-3 hover:theme-bg-quaternary/50 transition-colors duration-200 cursor-pointer aspect-square flex flex-col items-center justify-center text-center">
                  <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-green-600 rounded-lg flex items-center justify-center mb-3">
                    <span className="text-white font-bold text-sm">F</span>
                  </div>
                  <div className="font-medium theme-text-primary text-xs mb-2">Fyers</div>
                  <div className="text-xs theme-text-quaternary text-center leading-tight mb-3">Indian stock, indices and F&O discouned broker</div>
                  <button className="px-3 py-1 bg-[#2AB7A6] text-white text-xs font-medium rounded hover:bg-[#239B8A] transition-colors duration-200">
                    Connect
                  </button>
                </div>

                {/* Live Trading Studio Card */}
                  <div className="border theme-border rounded-lg p-3 hover:theme-bg-quaternary/50 transition-colors duration-200 cursor-pointer aspect-square flex flex-col items-center justify-center text-center">
                  <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg flex items-center justify-center mb-3">
                    <span className="text-white font-bold text-xs">LTS</span>
                  </div>
                  <div className="font-medium theme-text-primary text-sm mb-1">Live Trading Studio</div>
                  <div className="text-xs theme-text-quaternary text-center leading-tight mb-2">AI Automate Trades for Crypto, Forex, Stocks, Commodities</div>
                  <button className="px-3 py-1 bg-[#2AB7A6] text-white text-xs font-medium rounded hover:bg-[#239B8A] transition-colors duration-200">
                    Connect
                  </button>
                </div>
              </div>
            </div>

            {/* Header - Moved to bottom center */}
            <div className="text-sm font-medium theme-text-primary my-3 text-center">
              Connect through our trusted brokers
            </div>
          </div>
        )}

      </div>
    </div>
    </div>
  );
};

export default PositionPanel;
